import React from 'react'
import { Container } from '../../../../components'
import { CircularProgress, Typography, Tooltip } from '@material-ui/core'
import { Grid, Box } from '@material-ui/core'
import StarIcon from '@material-ui/icons/Star'
import FlagIcon from '@material-ui/icons/Flag'
import ScheduleIcon from '@material-ui/icons/Schedule'
import CropOriginalIcon from '@material-ui/icons/CropOriginal'
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined'
import AssignmentOutlinedIcon from '@material-ui/icons/AssignmentOutlined'
import { LoriText } from '../../../../navigation/layouts/Lori/Icons/LoriText'
import { ScheduleMethod } from './types'
import { useScheduleMethodSelectionStyles } from './styles'
import { useLoriState } from '../../../../contexts/LoriContext'
import { ezScheduleDetails } from '../../../../models/WorkOrder/types'
import { useTimezone } from '../../../../contexts/timezone'
import { formatScheduleDateInZone } from '../../../../helpers'

interface Props {
    setSelectedMethod: (method: ScheduleMethod) => void
    ezSchedule: ezScheduleDetails
    ezScheduleLoading: boolean
    canDuplicateTurn?: boolean
    duplicatableSchedulesLoading?: boolean
    hasInspection?: boolean
}

type BadgeLabel =
    | 'Recommended For New Users'
    | 'Beta'
    | 'Most Popular'
    | 'Legacy'
    | 'Time Saver'
    | 'Build from Inspection'

interface BadgeConfig {
    Icon: React.ElementType
    textColor: string
    borderColor: string
    background: string
}

const BADGE_CONFIG: Record<BadgeLabel, BadgeConfig> = {
    'Recommended For New Users': {
        Icon: StarIcon,
        textColor: '#2E7D32',
        borderColor: '#81C784',
        background:
            'linear-gradient(0deg, rgba(46, 125, 50, 0.10), rgba(46, 125, 50, 0.10)), #ffffff',
    },
    Beta: {
        Icon: FlagIcon,
        textColor: '#B26A00',
        borderColor: '#F4B546',
        background:
            'linear-gradient(0deg, rgba(245, 124, 0, 0.12), rgba(245, 124, 0, 0.12)), #FFFDE7',
    },
    'Most Popular': {
        Icon: CropOriginalIcon,
        textColor: '#00897B',
        borderColor: '#80CBC4',
        background:
            'linear-gradient(0deg, rgba(0, 137, 123, 0.10), rgba(0, 137, 123, 0.10)), #ffffff',
    },
    Legacy: {
        Icon: ScheduleIcon,
        textColor: '#6D4C41',
        borderColor: '#BCAAA4',
        background:
            'linear-gradient(0deg, rgba(109, 76, 65, 0.10), rgba(109, 76, 65, 0.10)), #ffffff',
    },
    'Time Saver': {
        Icon: FileCopyOutlinedIcon,
        textColor: '#1565C0',
        borderColor: '#90CAF9',
        background:
            'linear-gradient(0deg, rgba(21, 101, 192, 0.10), rgba(21, 101, 192, 0.10)), #ffffff',
    },
    'Build from Inspection': {
        Icon: AssignmentOutlinedIcon,
        textColor: '#6A1B9A',
        borderColor: '#CE93D8',
        background:
            'linear-gradient(0deg, rgba(106, 27, 154, 0.10), rgba(106, 27, 154, 0.10)), #ffffff',
    },
}

export const ScheduleMethodSelection = (props: Props) => {
    const {
        setSelectedMethod,
        ezSchedule,
        ezScheduleLoading,
        canDuplicateTurn = false,
        duplicatableSchedulesLoading = false,
        hasInspection = false,
    } = props
    const classes = useScheduleMethodSelectionStyles()
    const { createConversation } = useLoriState()
    const { currentTimezone, propertyTimezone } = useTimezone()
    const displayZone = propertyTimezone ?? currentTimezone

    if (ezScheduleLoading) {
        return (
            <Container className={classes.root}>
                <CircularProgress />
            </Container>
        )
    }
    const startDateStr = formatScheduleDateInZone(
        ezSchedule.start_date,
        displayZone,
        'MM/dd/yyyy',
    )
    const endDateStr = formatScheduleDateInZone(
        ezSchedule.end_date,
        displayZone,
        'MM/dd/yyyy',
    )

    const renderBadge = (label: BadgeLabel) => {
        const badge = BADGE_CONFIG[label]
        const Icon = badge.Icon

        return (
            <div
                className={classes.optionBadge}
                style={{
                    background: badge.background,
                    color: badge.textColor,
                    border: `1px solid ${badge.borderColor}`,
                }}
            >
                <Icon fontSize="small" />
                <span
                    className={classes.optionBadgeText}
                    style={{ color: badge.textColor }}
                >
                    {label}
                </span>
            </div>
        )
    }

    const duplicateDisabled = duplicatableSchedulesLoading || !canDuplicateTurn
    const walkDisabled = !hasInspection

    const walkCard = (
        <Box
            className={`${classes.optionBox} ${
                walkDisabled ? classes.optionBoxDisabled : ''
            }`}
            onClick={() => {
                if (walkDisabled) return
                setSelectedMethod('walk_scheduler')
            }}
        >
            {renderBadge('Build from Inspection')}
            <Typography className={classes.optionTitle}>
                Walk Scheduler
            </Typography>
            <Typography className={classes.optionDescription}>
                Mark services by area while walking the inspection, then
                schedule the turn
            </Typography>
        </Box>
    )

    const duplicateCard = (
        <Box
            className={`${classes.optionBox} ${
                duplicateDisabled ? classes.optionBoxDisabled : ''
            }`}
            onClick={() => {
                if (duplicateDisabled) return
                setSelectedMethod('duplicate_turn')
            }}
        >
            {renderBadge('Time Saver')}
            <Typography className={classes.optionTitle}>
                Duplicate Previous Turn
            </Typography>
            <Typography className={classes.optionDescription}>
                Copy units, services, and vendors from a prior turn, then
                confirm in Auto Scheduler
            </Typography>
        </Box>
    )

    return (
        <Container className={classes.root}>
            <div className={classes.headerBlock}>
                <Typography className={classes.headerTitle}>
                    Scheduling Method
                </Typography>
                <Typography className={classes.headerCaption}>
                    How would you like to schedule your project
                </Typography>
            </div>
            <Grid container className={classes.optionsContainer} spacing={3}>
                {/* Lori options */}
                <Grid item xs={12} md={6} className={classes.column}>
                    {/* Step by Step with Lori */}
                    <Box
                        className={classes.optionBox}
                        onClick={() => {
                            setSelectedMethod('lori_step')
                            createConversation({
                                agent: 'turn_step_wizard',
                                initial_message: `The schedule start date is ${startDateStr} and the end date is ${endDateStr}. Begin the process.`,
                            })
                        }}
                    >
                        {renderBadge('Recommended For New Users')}
                        <Typography className={classes.optionTitle}>
                            Step by Step with{' '}
                            <Box
                                component="span"
                                display="inline-flex"
                                alignItems="baseline"
                                justifyContent="center"
                                style={{ gap: 6 }}
                            >
                                <Box
                                    component="span"
                                    display="inline-flex"
                                    alignItems="center"
                                    style={{
                                        lineHeight: 0,
                                        transform: 'translateY(2px)',
                                    }}
                                >
                                    <LoriText width={62} height={22} />
                                </Box>
                            </Box>
                        </Typography>
                        <Typography className={classes.optionDescription}>
                            Our AI agent will walk you through scheduling the
                            project step by step.
                        </Typography>
                    </Box>
                    {/* Advanced Turn with Lori */}
                    <Box
                        className={classes.optionBox}
                        onClick={() => {
                            setSelectedMethod('lori_advanced')
                            createConversation({
                                agent: 'advanced_turn_wizard',
                                initial_message: `The schedule start date is ${startDateStr} and the end date is ${endDateStr}. Send the welcome message.`,
                            })
                        }}
                    >
                        {renderBadge('Beta')}
                        <Typography className={classes.optionTitle}>
                            Advanced Turn with{' '}
                            <Box
                                component="span"
                                display="inline-flex"
                                alignItems="baseline"
                                justifyContent="center"
                                style={{ gap: 6 }}
                            >
                                <Box
                                    component="span"
                                    display="inline-flex"
                                    alignItems="center"
                                    style={{
                                        lineHeight: 0,
                                        transform: 'translateY(2px)',
                                    }}
                                >
                                    <LoriText width={62} height={22} />
                                </Box>
                            </Box>
                        </Typography>
                        <Typography className={classes.optionDescription}>
                            For users with experience scheduling projects with
                            Lori
                        </Typography>
                    </Box>
                    {walkDisabled ? (
                        <Tooltip title="You have to attach an inspection to use this method.">
                            <span style={{ width: '100%', maxWidth: 360 }}>
                                {walkCard}
                            </span>
                        </Tooltip>
                    ) : (
                        walkCard
                    )}
                </Grid>

                {/* Non-Lori options */}
                <Grid item xs={12} md={6} className={classes.column}>
                    {/* Auto Scheduler */}
                    <Box
                        className={classes.optionBox}
                        onClick={() => {
                            setSelectedMethod('auto_scheduler')
                        }}
                    >
                        {renderBadge('Most Popular')}
                        <Typography className={classes.optionTitle}>
                            Auto Scheduler
                        </Typography>
                        <Typography className={classes.optionDescription}>
                            Configure Project and let the system Assign
                            Workorders
                        </Typography>
                    </Box>
                    {duplicateDisabled ? (
                        <Tooltip
                            title={
                                duplicatableSchedulesLoading
                                    ? 'Checking for previous turns…'
                                    : 'You don’t have any schedules that can be duplicated. Use Auto Scheduler on a turn first to create a template for next year.'
                            }
                        >
                            <span style={{ width: '100%', maxWidth: 360 }}>
                                {duplicateCard}
                            </span>
                        </Tooltip>
                    ) : (
                        duplicateCard
                    )}
                    {/* Bulk Assign */}
                    <Box
                        className={classes.optionBox}
                        onClick={() => {
                            setSelectedMethod('bulk_assign')
                        }}
                    >
                        {renderBadge('Legacy')}
                        <Typography className={classes.optionTitle}>
                            Bulk Assign
                        </Typography>
                        <Typography className={classes.optionDescription}>
                            Manually Assign Workorders
                        </Typography>
                    </Box>
                </Grid>
            </Grid>
        </Container>
    )
}
